/**
 * Custom hook for fetching and managing projects
 */
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import projectsService from '../services/projectsService';

export const useProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await projectsService.getAll();
      setProjects(data);
    } catch (err) {
      setError(err.message || 'Failed to load projects');
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const createProject = async (data) => {
    try {
      const newProject = await projectsService.create(data);
      setProjects((prev) => [newProject, ...prev]);
      toast.success('Project created successfully');
      return { success: true, data: newProject };
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to create project';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const updateProject = async (id, data) => {
    try {
      const updated = await projectsService.update(id, data);
      setProjects((prev) =>
        prev.map((p) => (p.id === id ? { ...p, ...updated } : p))
      );
      toast.success('Project updated successfully');
      return { success: true, data: updated };
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to update project';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const deleteProject = async (id) => {
    try {
      await projectsService.delete(id);
      setProjects((prev) => prev.filter((p) => p.id !== id));
      toast.success('Project deleted');
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to delete project';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  return {
    projects,
    loading,
    error,
    fetchProjects,
    createProject,
    updateProject,
    deleteProject,
  };
};

/**
 * Custom hook for fetching a single project with activities
 */
export const useProject = (projectId) => {
  const [project, setProject] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProject = useCallback(async () => {
    if (!projectId) return;
    
    try {
      setLoading(true);
      setError(null);
      const [projectData, activitiesData] = await Promise.all([
        projectsService.getById(projectId),
        projectsService.getActivities(projectId),
      ]);
      setProject(projectData);
      setActivities(activitiesData);
    } catch (err) {
      setError(err.message || 'Failed to load project');
      toast.error('Failed to load project');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  const createActivity = async (data) => {
    try {
      const newActivity = await projectsService.createActivity(projectId, data);
      setActivities((prev) => [...prev, newActivity]);
      toast.success('Activity created successfully');
      return { success: true, data: newActivity };
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to create activity';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  return {
    project,
    activities,
    loading,
    error,
    fetchProject,
    createActivity,
  };
};

export default useProjects;
